import { useState, useCallback, DragEvent } from "react"

interface UseDragAndDropOptions {
  onDrop: (file: File) => void | Promise<void>
  disabled?: boolean
  multiple?: boolean
}

export const useDragAndDrop = ({ onDrop, disabled = false, multiple = false }: UseDragAndDropOptions) => {
  const [isDragOver, setIsDragOver] = useState(false)

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
      
      if (!disabled) {
        setIsDragOver(true)
      }
    },
    [disabled]
  )

  const handleDragLeave = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
      
      // Only set to false if we're leaving the drop zone entirely
      // This prevents flickering when dragging over child elements
      const rect = event.currentTarget.getBoundingClientRect()
      const x = event.clientX
      const y = event.clientY
      
      if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
        setIsDragOver(false)
      }
    },
    []
  )

  const handleDrop = useCallback(
    async (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
      setIsDragOver(false)

      if (disabled) {
        return
      }

      const files = event.dataTransfer.files
      if (files && files.length > 0) {
        if (multiple) {
          // Handle multiple files if needed in the future
          for (let i = 0; i < files.length; i++) {
            await onDrop(files[i])
          }
        } else {
          // Handle single file (default)
          await onDrop(files[0])
        }
      }
    },
    [onDrop, disabled]
  )

  const dragHandlers = {
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
  }

  return {
    isDragOver,
    dragHandlers,
    setIsDragOver,
  }
}